/**
 * Leads Page — manage and track leads from conversations
 */
import EmptyState from '@/components/ui/EmptyState';
import PageHeader from '@/components/ui/PageHeader';
import SearchBar from '@/components/ui/SearchBar';
import StatusChip from '@/components/ui/StatusChip';
import PhoneField, { formatPhoneDisplay, isValidPhone } from '@/components/ui/PhoneField';
import adminApi, { FieldDefinition, ScoreBreakdownEntry } from '@/services/admin.api';
import DynamicFields from '@/components/common/DynamicFields';
import CustomFieldValues from '@/components/common/CustomFieldValues';
import SourceChip, { IngestionChip, ingestionLabel, sourceLabel } from '@/components/ui/SourceChip';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { useTerminology } from '@/hooks/useTerminology';
import { useAuth } from '@/hooks/useAuth';
import { useWebSocket, LeadScoredEvent } from '@/hooks/useWebSocket';
import {
  Add,
  AssignmentInd,
  ChatBubbleOutline,
  Close,
  DeleteOutline,
  Download,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MoreVert,
  PersonAdd,
  UploadFile,
  ViewColumn,
  Visibility,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  ListSubheader,
  Menu,
  MenuItem,
  Pagination,
  Popover,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useListViewScroll } from '@/hooks/admin/useListViewScroll';
import {
  field,
  resolveUpdate,
  Updater,
  useUrlListState,
} from '@/hooks/admin/useUrlListState';

interface LeadCustomAttribute {
  key: string;
  value: string | number | boolean;
  updatedAt?: string;
}

interface LeadExternalAttribute {
  clientId: string;
  clientName: string;
  attributes: Array<{ key: string; value: any }>;
  tags?: string[];
  externalLeadId?: string;
  lastSyncedAt?: string;
}

interface Lead {
  _id: string;
  // Optional: a lead's identity can be phone OR email (unlike Contact, which always has a
  // phone) — CSV import and the external-gateway upsert can both create email-only leads.
  customerPhone?: string;
  customerName?: string;
  customerEmail?: string;
  customerCompany?: string;
  customerCountry?: string;
  source: 'whatsapp' | 'hubspot' | 'meta' | 'excel' | 'manual' | 'api';
  /** Drill-down for `source` — e.g. the external client's name for source==='api'. */
  sourceDetail?: string;
  status: 'new' | 'contacted' | 'qualified' | 'unqualified' | 'converted' | 'lost' | 'archived';
  priority?: 'low' | 'medium' | 'high' | 'hot';
  score?: number;
  title?: string;
  description?: string;
  interest?: string;
  estimatedValue?: number;
  budget?: string;
  notes?: string;
  tags?: string[];
  customAttributes?: LeadCustomAttribute[];
  externalAttributes?: LeadExternalAttribute[];
  assignedTo?: any;
  lastContactAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** Trims, drops empties, and dedupes case-insensitively (first-seen casing wins). */
function cleanTags(rawTags: string[]): string[] {
  const seen = new Set<string>();
  const cleaned: string[] = [];
  for (const raw of rawTags) {
    const trimmed = String(raw).trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    cleaned.push(trimmed);
  }
  return cleaned;
}

/** Custom-attribute keys can be reused across many leads; collisions are only invalid within a single lead's own form. */
const MAX_CUSTOM_FIELDS_PER_LEAD = 50;

const priorityColor: Record<string, string> = {
  hot: '#ef4444',
  high: '#f97316',
  medium: '#f59e0b',
  low: '#94a3b8',
};

const LEAD_STATUS_OPTIONS = [
  'new',
  'contacted',
  'qualified',
  'unqualified',
  'converted',
  'lost',
  'archived',
] as const;

/** Mirrors Contacts.tsx's CONTACTS_PAGE_SIZE — same page size across admin list views. */
const LEADS_PAGE_SIZE = 20;

/** One label/value row in the detail popup. Renders NOTHING when the value is
 *  empty, so a sparse lead shows a short honest panel instead of a wall of
 *  em-dashes — the panel's job is to surface what exists, not to list every
 *  field the schema allows. */
const DetailField: React.FC<{
  label: string;
  // `| undefined` is explicit because this project sets
  // `exactOptionalPropertyTypes`, under which `value?: string` does NOT accept an
  // explicitly-passed undefined — and every call site here passes one.
  value?: string | null | undefined;
  dense?: boolean | undefined;
}> = ({ label, value, dense }) => {
  if (!value || !String(value).trim()) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: dense ? 96 : 120, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
        {value}
      </Typography>
    </Box>
  );
};

/** A titled group of DetailFields. Hidden entirely when every child rendered
 *  null, so an empty section never leaves a stray heading behind. */
const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => {
  const hasContent = React.Children.toArray(children).some(
    (c) => React.isValidElement(c) && c.props?.value && String(c.props.value).trim()
  );
  if (!hasContent) return null;
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
        {title}
      </Typography>
      <Stack spacing={0.5}>{children}</Stack>
    </Box>
  );
};

/** Static label above an input — same look as PhoneField's label, so every field in a form lines up
 *  (a form mixing floating and stacked labels is what made the old dialog look uneven). */
const Labeled: React.FC<{
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, required, hint, children }) => (
  <Box>
    <Typography
      component="label"
      sx={{ display: 'block', mb: 0.75, fontSize: '0.8125rem', fontWeight: 600, color: '#334155' }}
    >
      {label}
      {required && (
        <Typography component="span" color="error" ml={0.5}>
          *
        </Typography>
      )}
    </Typography>
    {children}
    {hint && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

/** A titled block of a form, separated by a hairline. */
const FormSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box>
    <Typography
      variant="overline"
      sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.8, color: 'text.secondary', lineHeight: 1.6 }}
    >
      {title}
    </Typography>
    <Divider sx={{ mb: 2, mt: 0.25 }} />
    {children}
  </Box>
);

/** Small local relative-time formatter — this file has no shared one to reuse. */
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString();
}

const splitCsvLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const next = i + 1 < line.length ? line[i + 1] : '';

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  result.push(current.trim());
  return result;
};

const normalizeHeader = (header: string): string =>
  header
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '');

const LEAD_FIELD_ALIASES: Record<string, string> = {
  name: 'customerName',
  fullname: 'customerName',
  customername: 'customerName',
  phone: 'customerPhone',
  phonenumber: 'customerPhone',
  mobile: 'customerPhone',
  customerphone: 'customerPhone',
  whatsapp: 'customerPhone',
  email: 'customerEmail',
  emailaddress: 'customerEmail',
  customeremail: 'customerEmail',
  company: 'customerCompany',
  organization: 'customerCompany',
  customercompany: 'customerCompany',
  country: 'customerCountry',
  customercountry: 'customerCountry',
  title: 'title',
  enquirytitle: 'title',
  subject: 'title',
  description: 'description',
  enquirydescription: 'description',
  interest: 'interest',
  value: 'estimatedValue',
  estimatedvalue: 'estimatedValue',
  budget: 'budget',
  notes: 'notes',
  tags: 'tags',
  tag: 'tags',
  segment: 'tags',
};

const toFieldMapping = (headers: string[]): Record<string, string> => {
  const mapping: Record<string, string> = {};
  for (const header of headers) {
    const normalized = normalizeHeader(header);
    if (LEAD_FIELD_ALIASES[normalized]) {
      mapping[header] = LEAD_FIELD_ALIASES[normalized];
    }
  }
  return mapping;
};

const parseCsvFile = async (
  file: File
): Promise<{ rows: Record<string, string>[]; fieldMapping: Record<string, string> }> => {
  const text = await file.text();
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return { rows: [], fieldMapping: {} };
  }

  const [firstLine] = lines;
  if (!firstLine) {
    return { rows: [], fieldMapping: {} };
  }

  const headers = splitCsvLine(firstLine);
  const rows: Record<string, string>[] = lines.slice(1).map((line) => {
    const values = splitCsvLine(line);
    const row: Record<string, string> = {};
    headers.forEach((header, index) => {
      row[header] = values[index] ?? '';
    });
    return row;
  });

  return {
    rows,
    fieldMapping: toFieldMapping(headers),
  };
};

/**
 * Extracts one sheet of an already-read .xlsx/.xls workbook into the same `{ rows, fieldMapping }`
 * shape the CSV path produces — mirrors Contacts.tsx's parseContactsExcelSheet.
 */
const parseLeadsExcelSheet = (
  workbook: XLSX.WorkBook,
  sheetName: string
): { rows: Record<string, string>[]; fieldMapping: Record<string, string> } => {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return { rows: [], fieldMapping: {} };

  const aoa = XLSX.utils.sheet_to_json<Array<string | number | null>>(sheet, {
    header: 1,
    defval: '',
    raw: false,
    blankrows: false,
  });
  if (aoa.length < 2) return { rows: [], fieldMapping: {} };

  const headers = (aoa[0] || []).map((h) => String(h ?? '').trim());
  const rows: Record<string, string>[] = aoa
    .slice(1)
    .map((values) => {
      const row: Record<string, string> = {};
      headers.forEach((header, index) => {
        const cell = (values as Array<string | number | null>)[index];
        row[header] = cell == null ? '' : String(cell).trim();
      });
      return row;
    })
    .filter((row) => Object.values(row).some((v) => v !== ''));

  return { rows, fieldMapping: toFieldMapping(headers) };
};

/** Reads an .xlsx/.xls file into a workbook so its sheet names can be listed before picking one. */
const readLeadsWorkbook = async (file: File): Promise<XLSX.WorkBook> => {
  const buffer = await file.arrayBuffer();
  return XLSX.read(buffer, { type: 'array' });
};

/** Windows offered by the list's time filter. Values must match the backend's
 *  `LeadDateRange` exactly — it resolves them against its own clock so "today"
 *  never depends on the browser's idea of midnight. Filters on ACTIVITY, so a
 *  months-old lead that just re-enquired correctly shows under "Today". */
const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'Any time' },
  { value: 'lastHour', label: 'Last hour' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7d', label: 'Last 7 days' },
  { value: 'last30d', label: 'Last 30 days' },
  { value: 'thisMonth', label: 'This month' },
];

/** Toggleable columns, in render order. `contact` and the Actions cell are NOT
 *  here: one is the row's identity and the other is how you act on it, so
 *  neither can be switched off without leaving an unusable table. Mirrors
 *  Contacts' Table Columns, which omits its own name column for the same reason. */
const LEAD_COLUMNS = [
  { key: 'enquiry', label: 'Enquiry' },
  { key: 'tags', label: 'Tags' },
  { key: 'source', label: 'Source' },
  { key: 'lastActivity', label: 'Last Activity' },
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'score', label: 'Score' },
  { key: 'assignedTo', label: 'Assigned To' },
] as const;

type LeadColumnKey = (typeof LEAD_COLUMNS)[number]['key'];
type LeadColumnVisibility = Record<LeadColumnKey, boolean>;

const DEFAULT_LEAD_COLUMNS: LeadColumnVisibility = {
  enquiry: true,
  tags: true,
  source: true,
  lastActivity: true,
  status: true,
  priority: true,
  score: true,
  assignedTo: true,
};

/** Separate from the Contacts key so the two lists' preferences can't collide. */
const LEAD_COLUMNS_STORAGE_KEY = 'leads_visible_columns';

const LEAD_CSV_TEMPLATE =
  'name,phone,email,company,country,title,description,interest,value,budget,notes,tags\n' +
  'Jane Doe,+919876543210,jane@example.com,Acme Inc,IN,Website pricing enquiry,Asked about the Pro plan,Pricing,5000,50000-100000,Follow up next week,inbound;hot\n';

/**
 * CSV-import wire convention: a fieldMapping value of "custom:<key>" routes that column
 * into customAttributes.<key> instead of a fixed lead field. Mirrors
 * LEAD_CUSTOM_FIELD_MAPPING_PREFIX in the backend's lead.service.ts (no shared types
 * package between the two apps).
 */
const CUSTOM_FIELD_MAPPING_PREFIX = 'custom:';
const CUSTOM_FIELD_SELECT_VALUE = 'custom';

const LEAD_IMPORT_TARGET_FIELD_OPTIONS = [
  { value: '', label: "Don't import" },
  { value: 'customerName', label: 'Name' },
  { value: 'customerPhone', label: 'Phone' },
  { value: 'customerEmail', label: 'Email' },
  { value: 'customerCompany', label: 'Company' },
  { value: 'customerCountry', label: 'Country' },
  { value: 'title', label: 'Enquiry Title' },
  { value: 'description', label: 'Enquiry Description' },
  { value: 'interest', label: 'Interest' },
  { value: 'estimatedValue', label: 'Estimated Value' },
  { value: 'budget', label: 'Budget' },
  { value: 'notes', label: 'Notes' },
  { value: 'tags', label: 'Tags' },
  { value: CUSTOM_FIELD_SELECT_VALUE, label: 'Custom field…' },
];

/** Status defaults to 'new' — the list opens on untriaged enquiries. A deep
 *  link that needs the whole set says so explicitly (`&status=all`), which is
 *  clearer than the conditional default this used to carry. */
const LEADS_URL_SPEC = {
  search: field.str(),
  status: field.enumOf(
    ['all', 'new', 'contacted', 'qualified', 'unqualified', 'converted', 'lost', 'archived'] as const,
    'new'
  ),
  priority: field.enumOf(['all', 'hot', 'high', 'medium', 'low'] as const, 'all'),
  dateRange: field.enumOf(
    ['all', 'lastHour', 'today', 'yesterday', 'last7d', 'last30d', 'thisMonth'] as const,
    'all'
  ),
  tags: field.csv(),
  page: field.page(),
  /** Deep link: ?lead=<id> opens that lead's detail popup on arrival (e.g. from Web Captures). */
  lead: field.str(),
};

const Leads: React.FC = () => {
  // Filters, search and page live in the URL, so opening a lead and coming back
  // keeps the view — and a filtered list is a link you can send someone. The
  // ?search= deep link from the contact detail page is just one field of this
  // spec now, rather than a separate bootstrap path.
  const { state: query, patch: patchQuery } = useUrlListState(LEADS_URL_SPEC);
  const [leads, setLeads] = useState<Lead[]>([]);
  // Live score/priority updates from the backend's background rescore (no refresh needed).
  const fetchLeadsRef = useRef<() => void>(() => {});
  const { user: wsUser, tenant: wsTenant } = useAuth();
  // Taking a lead from someone else needs Full on Leads (enforced server-side too) — the UI
  // only mirrors that so the control is not offered where it would just be refused.
  const { hasPermission } = usePermissionAccess();
  const canReassignOthers = hasPermission('activity.leads', 'full');
  const [assignMenu, setAssignMenu] = useState<{ anchor: HTMLElement; lead: Lead } | null>(null);
  const [actionErrorMsg, setActionErrorMsg] = useState('');
  useWebSocket(wsUser?.tenantId || wsTenant?.id, {
    onLeadScored: (data: LeadScoredEvent) => {
      if (data.refresh) {
        fetchLeadsRef.current();
        return;
      }
      const byId = new Map((data.leads ?? []).map((l) => [l.leadId, l]));
      if (!byId.size) return;
      setLeads((prev) =>
        prev.map((l) => {
          const u = byId.get(l._id);
          if (!u) return l;
          const next: Lead = { ...l, score: u.score };
          if (u.priority) next.priority = u.priority as NonNullable<Lead['priority']>;
          return next;
        })
      );
    },
  });
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  // The input stays local so typing is instant; only the debounced value is
  // committed to the query. Leads previously had no debounce at all and fired a
  // request per keystroke.
  const [search, setSearch] = useState(query.search);
  const debouncedSearch = query.search;
  // The Select handlers hand over a plain `string`. Casting here is safe because
  // the dropdowns only offer values the spec lists, and a hand-edited URL is
  // caught by the codec's own membership check on the way in.
  const filterStatus: string = query.status;
  const setFilterStatus = (value: string) =>
    patchQuery({ status: value as typeof query.status });
  const filterPriority: string = query.priority;
  const setFilterPriority = (value: string) =>
    patchQuery({ priority: value as typeof query.priority });
  const filterDateRange: string = query.dateRange;
  const setFilterDateRange = (value: string) =>
    patchQuery({ dateRange: value as typeof query.dateRange });
  const filterTags = query.tags;
  const setFilterTags = (update: Updater<string[]>) =>
    patchQuery({ tags: resolveUpdate(update, query.tags) });
  const page = query.page;
  const setPage = (value: number) => patchQuery({ page: value });
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [leadStats, setLeadStats] = useState<{
    total: number;
    byStatus: {
      new: number;
      contacted: number;
      qualified: number;
      unqualified: number;
      converted: number;
      lost: number;
    };
  } | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  // The "+N" overflow chip on a lead row's Tags column — click opens a popover with
  // every tag on that lead, not just the first 2. Same shared-anchor pattern as the
  // row actions `anchorEl`/`selectedLeadId` above, kept as its own pair so opening one
  // never closes the other.
  const [tagsAnchorEl, setTagsAnchorEl] = useState<null | HTMLElement>(null);
  const [tagsPopoverLeadId, setTagsPopoverLeadId] = useState<string | null>(null);
  // Full-detail popup. The inline row expander is deliberately a SUMMARY — it
  // shows the rollup's headline fields and only each enquiry's title/description.
  // Everything else a submission carried (its own interest, budget, notes, tags,
  // channel, custom fields) was stored but unreachable in the UI, which is what
  // made an older enquiry impossible to inspect.
  const [detailLead, setDetailLead] = useState<Lead | null>(null);
  // Highest-score-first ordering (default stays newest-first: this is a live inbox).
  const [sortByScore, setSortByScore] = useState(false);
  // "Why this score?" popover.
  const [whyAnchor, setWhyAnchor] = useState<HTMLElement | null>(null);
  const [whyData, setWhyData] = useState<{ score: number; breakdown: ScoreBreakdownEntry[] } | null>(null);
  const [whyLoading, setWhyLoading] = useState(false);
  const openWhy = async (el: HTMLElement, leadId: string) => {
    setWhyAnchor(el);
    setWhyData(null);
    setWhyLoading(true);
    try {
      const data = await adminApi.getLeadScoreBreakdown(leadId);
      setWhyData(data);
      // The popover reads the stored score; make the row agree with it.
      setLeads((prev) =>
        prev.map((l) => {
          if (l._id !== leadId) return l;
          const next: Lead = { ...l, score: data.score };
          if (data.priority) next.priority = data.priority as NonNullable<Lead['priority']>;
          return next;
        })
      );
    } catch {
      setWhyData(null);
    } finally {
      setWhyLoading(false);
    }
  };
  // Which row's contact lookup is in flight (the phone→contact resolution is a
  // network hop, so the icon must show it isn't a dead click), and a message for
  // the cases where there simply is no contact record to open.
  const [contactLookupLeadId, setContactLookupLeadId] = useState<string | null>(null);
  const [leadNavError, setLeadNavError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // ── Table columns ────────────────────────────────────────────────────────
  // `columnDraft` exists so Cancel actually cancels. Contacts' equivalent binds
  // its checkboxes straight to the live state, so the table re-renders as you
  // tick and Cancel only skips the localStorage write — the visible change has
  // already happened. Editing a draft and committing on Save avoids that.
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] =
    useState<LeadColumnVisibility>(DEFAULT_LEAD_COLUMNS);
  const [columnDraft, setColumnDraft] = useState<LeadColumnVisibility>(DEFAULT_LEAD_COLUMNS);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LEAD_COLUMNS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<LeadColumnVisibility>;
      // Spread over the defaults rather than replacing them: a stored blob from
      // an older build won't have keys added since, and those must stay visible
      // instead of coming back as `undefined` (falsy ⇒ silently hidden).
      setVisibleColumns({ ...DEFAULT_LEAD_COLUMNS, ...parsed });
    } catch {
      // Corrupt/unavailable storage must never blank the table.
    }
  }, []);

  const openColumnsDialog = () => {
    setColumnDraft(visibleColumns);
    setColumnsOpen(true);
  };

  const saveColumns = () => {
    setVisibleColumns(columnDraft);
    try {
      localStorage.setItem(LEAD_COLUMNS_STORAGE_KEY, JSON.stringify(columnDraft));
    } catch {
      // Private mode / quota — the choice still applies for this session.
    }
    setColumnsOpen(false);
  };

  /** Keeps the expanded-detail row's colSpan correct as columns are toggled —
   *  a hardcoded number silently misaligns the panel the moment one is hidden.
   *  +3 = checkbox, expander, contact; +1 = the Actions cell. */
  const visibleColumnCount = useMemo(
    () => 3 + LEAD_COLUMNS.filter((c) => visibleColumns[c.key]).length + 1,
    [visibleColumns]
  );
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(false);

  // Scroll offset comes back with the query, so opening a lead and returning
  // does not send you to the top of a long list.
  useListViewScroll(!loading && leads.length > 0);
  const [assigning, setAssigning] = useState(false);
  const [expandedLeadIds, setExpandedLeadIds] = useState<Set<string>>(new Set());
  const [enquiryHistory, setEnquiryHistory] = useState<Record<string, any[]>>({});
  const [enquiryHistoryTotal, setEnquiryHistoryTotal] = useState<Record<string, number>>({});
  const [enquiryLoading, setEnquiryLoading] = useState<Record<string, boolean>>({});
  const [statusUpdating, setStatusUpdating] = useState<Record<string, boolean>>({});
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergePrimaryId, setMergePrimaryId] = useState('');
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState('');
  const [addLeadDialogOpen, setAddLeadDialogOpen] = useState(false);
  const [leadFieldDefs, setLeadFieldDefs] = useState<FieldDefinition[]>([]);
  const [definedValues, setDefinedValues] = useState<Record<string, string>>({});
  useEffect(() => {
    adminApi
      .getFieldDefinitions('lead')
      .then(setLeadFieldDefs)
      .catch(() => {});
  }, []);
  // Vertical copy (e.g. a travel tenant relabels "Lead" -> "Enquiry").
  const { terminology } = useTerminology();
  const leadWord = terminology.lead;
  const [addingLead, setAddingLead] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importRows, setImportRows] = useState<Record<string, string>[]>([]);
  const [importMapping, setImportMapping] = useState<Record<string, string>>({});
  const [importTags, setImportTags] = useState<string[]>([]);
  const [importFileName, setImportFileName] = useState('');
  const [importWorkbook, setImportWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [importSheetNames, setImportSheetNames] = useState<string[]>([]);
  const [importSelectedSheet, setImportSelectedSheet] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importResult, setImportResult] = useState<{
    imported: number;
    updated: number;
    skipped: number;
    errors: Array<{ row: number; error: string }>;
  } | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [attributeKeys, setAttributeKeys] = useState<string[]>([]);
  const [addError, setAddError] = useState('');
  // ── Bulk assign ──────────────────────────────────────────────────────────
  const [bulkLeadAssignOpen, setBulkLeadAssignOpen] = useState(false);
  const [bulkLeadAssignUserId, setBulkLeadAssignUserId] = useState('');
  const [bulkLeadAssignSaving, setBulkLeadAssignSaving] = useState(false);
  const [bulkLeadAssignError, setBulkLeadAssignError] = useState('');
  const [bulkLeadSuccessMsg, setBulkLeadSuccessMsg] = useState('');
  // ── Quick assign (single row via context menu) ───────────────────────────
  const [quickAssignLead, setQuickAssignLead] = useState<Lead | null>(null);
  const [quickAssignLeadUserId, setQuickAssignLeadUserId] = useState('');
  const [quickAssignLeadSaving, setQuickAssignLeadSaving] = useState(false);
  const [quickAssignLeadError, setQuickAssignLeadError] = useState('');
  const [newLead, setNewLead] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerCompany: '',
    title: '',
    description: '',
    interest: '',
    source: 'manual' as Lead['source'],
    status: 'new' as Lead['status'],
    notes: '',
    tags: [] as string[],
    assignedTo: '',
    customAttributes: [] as Array<{ key: string; value: string }>,
  });

  const fetchLeads = async () => {
    setLoading(true);
    try {
      // Newest enquiry first by default — this is a live inbox, not a leaderboard.
      const params: any = { page, limit: LEADS_PAGE_SIZE, sortBy: sortByScore ? 'score' : 'createdAt', sortOrder: 'desc' };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (filterDateRange !== 'all') params.dateRange = filterDateRange;
      if (filterTags.length) params.tags = filterTags.join(',');
      const response = await adminApi.getLeads(params);
      setLeads(response.data || []);
      setTotalCount(response.pagination?.total ?? 0);
      setTotalPages(Math.max(1, response.pagination?.totalPages ?? 1));
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  // Tenant-wide totals for the stat cards — deliberately independent of whatever
  // filter/page is currently applied to the table below (getLeadStats has no status/
  // priority/tag/search params, it's a full-tenant aggregation).
  const fetchLeadStats = async () => {
    try {
      const stats = await adminApi.getLeadStats({ suppressPermissionLock: true });
      setLeadStats(stats);
    } catch (error) {
      console.error('Error fetching lead stats:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await adminApi.getTeamDirectory({ suppressPermissionLock: true });
      setTeamMembers(response || []);
    } catch (error) {
      console.error('Error fetching team:', error);
    }
  };

  const fetchTagsAndAttributeKeys = () => {
    adminApi
      .getLeadTags()
      .then(setAllTags)
      .catch(() => {});
    adminApi
      .getLeadAttributeKeys()
      .then(setAttributeKeys)
      .catch(() => {});
  };

  // Debounce the search box before it reaches the query — and therefore the API.
  // A no-op patch (this firing on mount with the value it just read out of the
  // URL) is dropped by the hook, so it costs no extra request.
  useEffect(() => {
    const timer = setTimeout(() => patchQuery({ search: search.trim() }), 300);
    return () => clearTimeout(timer);
  }, [search, patchQuery]);

  fetchLeadsRef.current = fetchLeads;
  useEffect(() => {
    fetchLeads();
  }, [debouncedSearch, filterStatus, filterPriority, filterDateRange, filterTags, page, sortByScore]);
  // Returning to page 1 on a filter change is handled by the hook.
  useEffect(() => {
    fetchTeamMembers();
    fetchTagsAndAttributeKeys();
    fetchLeadStats();
  }, []);

  const addCustomAttributeField = () => {
    setNewLead((prev) => ({
      ...prev,
      customAttributes: [...prev.customAttributes, { key: '', value: '' }],
    }));
  };

  const updateCustomAttributeField = (
    index: number,
    patch: Partial<{ key: string; value: string }>
  ) => {
    setNewLead((prev) => {
      const current = prev.customAttributes[index];
      if (!current) return prev;
      const next = [...prev.customAttributes];
      next[index] = { key: patch.key ?? current.key, value: patch.value ?? current.value };
      return { ...prev, customAttributes: next };
    });
  };

  const removeCustomAttributeField = (index: number) => {
    setNewLead((prev) => ({
      ...prev,
      customAttributes: prev.customAttributes.filter((_, i) => i !== index),
    }));
  };

  const handleAssignLead = async () => {
    if (!selectedLeadId || !selectedUserId) return;
    setAssigning(true);
    try {
      await adminApi.assignLead(selectedLeadId, selectedUserId);
      setAssignDialogOpen(false);
      fetchLeads();
      fetchLeadStats();
    } catch (e) {
      console.error(e);
    } finally {
      setAssigning(false);
    }
  };

  const handleAddLead = async () => {
    if (newLead.customerPhone && !isValidPhone(newLead.customerPhone)) {
      setAddError('Please enter a valid phone number with country code (e.g. +91 98765 43210).');
      return;
    }

    const customAttributes = newLead.customAttributes
      .map((field) => ({ key: field.key.trim(), value: field.value.trim() }))
      .filter((field) => field.key);
    const seenKeys = new Set<string>();
    for (const field of customAttributes) {
      const lowerKey = field.key.toLowerCase();
      if (seenKeys.has(lowerKey)) {
        setAddError(`Custom field "${field.key}" is entered more than once.`);
        return;
      }
      seenKeys.add(lowerKey);
    }

    // Merge the vertical/defined field values (typed inputs) into customAttributes —
    // defined keys take precedence over any free-form entry with the same key.
    const definedEntries = leadFieldDefs
      .map((d) => ({ key: d.key, value: String(definedValues[d.key] ?? '').trim() }))
      .filter((e) => e.value);
    const definedKeys = new Set(definedEntries.map((e) => e.key.toLowerCase()));
    const mergedAttributes = [
      ...definedEntries,
      ...customAttributes.filter((f) => !definedKeys.has(f.key.toLowerCase())),
    ];

    setAddError('');
    setAddingLead(true);
    try {
      const { assignedTo, ...leadFields } = newLead;
      await adminApi.createLead({
        ...leadFields,
        ...(assignedTo ? { assignedTo } : {}),
        tags: cleanTags(newLead.tags),
        customAttributes: mergedAttributes,
      });
      setAddLeadDialogOpen(false);
      setNewLead({
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        customerCompany: '',
        title: '',
        description: '',
        interest: '',
        source: 'manual',
        status: 'new',
        notes: '',
        tags: [],
        assignedTo: '',
        customAttributes: [],
      });
      setDefinedValues({});
      fetchLeads();
      fetchLeadStats();
      fetchTagsAndAttributeKeys();
    } catch (e: any) {
      setAddError(e?.response?.data?.error?.message || 'Failed to add lead');
      console.error(e);
    } finally {
      setAddingLead(false);
    }
  };

  // Every CSV header, including ones the auto-detector didn't recognize — every row
  // object carries all headers as keys regardless of mapping.
  const importHeaders = useMemo(() => Object.keys(importRows[0] || {}), [importRows]);

  // The tenant's defined lead fields (vertical/custom) offered as first-class
  // labeled import targets, mapped as `custom:<key>` so the existing
  // customAttributes import path stores them — no backend change needed.
  const importDefinedOptions = useMemo(
    () =>
      leadFieldDefs.map((d) => ({
        value: `${CUSTOM_FIELD_MAPPING_PREFIX}${d.key}`,
        label: `${d.label} (defined)`,
      })),
    [leadFieldDefs]
  );
  const importDefinedValues = useMemo(
    () => new Set(importDefinedOptions.map((o) => o.value)),
    [importDefinedOptions]
  );
  const importTargetOptions = useMemo(() => {
    const custom = LEAD_IMPORT_TARGET_FIELD_OPTIONS[LEAD_IMPORT_TARGET_FIELD_OPTIONS.length - 1]!;
    const base = LEAD_IMPORT_TARGET_FIELD_OPTIONS.slice(0, -1);
    return [...base, ...importDefinedOptions, custom];
  }, [importDefinedOptions]);

  const getImportFieldSelectValue = (header: string): string => {
    const mapped = importMapping[header];
    if (!mapped) return '';
    // A defined-field target selects its own labeled option; only free-form
    // custom fields collapse to the generic "Custom field…" + Autocomplete.
    if (importDefinedValues.has(mapped)) return mapped;
    return mapped.startsWith(CUSTOM_FIELD_MAPPING_PREFIX) ? CUSTOM_FIELD_SELECT_VALUE : mapped;
  };

  const setImportFieldMapping = (header: string, value: string) => {
    setImportMapping((prev) => {
      const next = { ...prev };
      if (!value) {
        delete next[header];
      } else if (value === CUSTOM_FIELD_SELECT_VALUE) {
        next[header] = CUSTOM_FIELD_MAPPING_PREFIX;
      } else {
        next[header] = value;
      }
      return next;
    });
  };

  const setImportCustomFieldKey = (header: string, key: string) => {
    setImportMapping((prev) => ({ ...prev, [header]: `${CUSTOM_FIELD_MAPPING_PREFIX}${key}` }));
  };

  const openImportLeads = () => {
    setImportRows([]);
    setImportMapping({});
    setImportTags([]);
    setImportFileName('');
    setImportWorkbook(null);
    setImportSheetNames([]);
    setImportSelectedSheet('');
    setImportError('');
    setImportResult(null);
    setImportDialogOpen(true);
  };

  /** Loads a sheet's rows into the mapping UI; used both on file pick and on sheet switch. */
  const loadLeadsSheet = (workbook: XLSX.WorkBook, sheetName: string) => {
    const { rows, fieldMapping } = parseLeadsExcelSheet(workbook, sheetName);
    if (rows.length === 0) {
      setImportError(
        'No rows found in this sheet. Make sure it has a header row plus at least one data row.'
      );
      setImportRows([]);
      setImportMapping({});
      return;
    }
    const mappedTargets = Object.values(fieldMapping);
    if (!mappedTargets.includes('customerPhone') && !mappedTargets.includes('customerEmail')) {
      setImportError(
        'Could not find a "phone" or "email" column. Expected a header like "phone", "mobile", or "email".'
      );
    } else {
      setImportError('');
    }
    setImportRows(rows);
    setImportMapping(fieldMapping);
  };

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError('');
    setImportResult(null);
    setImportFileName(file.name);
    setImportWorkbook(null);
    setImportSheetNames([]);
    setImportSelectedSheet('');
    try {
      const isExcel = /\.(xlsx|xls)$/i.test(file.name);
      if (!isExcel) {
        const { rows, fieldMapping } = await parseCsvFile(file);
        if (rows.length === 0) {
          setImportError(
            'No rows found in this file. Make sure it has a header row plus at least one data row.'
          );
          setImportRows([]);
          setImportMapping({});
          return;
        }
        const mappedTargets = Object.values(fieldMapping);
        if (!mappedTargets.includes('customerPhone') && !mappedTargets.includes('customerEmail')) {
          setImportError(
            'Could not find a "phone" or "email" column. Expected a header like "phone", "mobile", or "email".'
          );
        }
        setImportRows(rows);
        setImportMapping(fieldMapping);
        return;
      }

      const workbook = await readLeadsWorkbook(file);
      const sheetNames = workbook.SheetNames;
      const firstSheetName = sheetNames[0];
      if (!firstSheetName) {
        setImportError('This workbook has no sheets.');
        setImportRows([]);
        setImportMapping({});
        return;
      }
      setImportWorkbook(workbook);
      setImportSheetNames(sheetNames);
      setImportSelectedSheet(firstSheetName);
      loadLeadsSheet(workbook, firstSheetName);
    } catch (err: any) {
      setImportError(err.message || 'Failed to read file');
    }
  };

  const handleImportSheetChange = (sheetName: string) => {
    setImportSelectedSheet(sheetName);
    setImportResult(null);
    if (importWorkbook) loadLeadsSheet(importWorkbook, sheetName);
  };

  const handleImport = async () => {
    if (importRows.length === 0) return;
    setImporting(true);
    setImportError('');
    try {
      const result = await adminApi.importLeadsFromExcel(importRows, importMapping, {
        fileName: importFileName,
        tags: cleanTags(importTags),
      });
      setImportResult(result);
      fetchLeads();
      fetchLeadStats();
      fetchTagsAndAttributeKeys();
    } catch (e: any) {
      setImportError(
        e?.response?.data?.error || e?.response?.data?.error?.message || 'Failed to import leads'
      );
      console.error(e);
    } finally {
      setImporting(false);
    }
  };

  const downloadLeadsTemplate = () => {
    const blob = new Blob([LEAD_CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'leads-import-template.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteLead = async () => {
    if (!selectedLeadId) return;
    const confirmed = window.confirm('Delete this lead permanently?');
    if (!confirmed) return;
    try {
      await adminApi.deleteLead(selectedLeadId);
      setAnchorEl(null);
      setSelectedLeadId(null);
      fetchLeads();
      fetchLeadStats();
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  // Extracted so a re-expand can force a refetch, not just the first expand.
  const fetchEnquiryHistory = (leadId: string) => {
    setEnquiryLoading((p) => ({ ...p, [leadId]: true }));
    adminApi
      .getLeadEnquiries(leadId, { page: 1, limit: 20 })
      .then((res) => {
        setEnquiryHistory((p) => ({ ...p, [leadId]: res.data || [] }));
        setEnquiryHistoryTotal((p) => ({
          ...p,
          [leadId]: res.pagination?.total ?? (res.data || []).length,
        }));
      })
      .catch((err) => {
        console.error('Error fetching enquiry history:', err);
        setEnquiryHistory((p) => ({ ...p, [leadId]: [] }));
        setEnquiryHistoryTotal((p) => ({ ...p, [leadId]: 0 }));
      })
      .finally(() => setEnquiryLoading((p) => ({ ...p, [leadId]: false })));
  };

  // Opening the popup must not depend on the row having been expanded first —
  // fetch the history if this lead has none cached, and refresh it if it does,
  // for the same staleness reason the expander refetches every time.
  // Only the fields the background rescore/status flow rewrites; omits undefined ones so the
  // merge stays valid under exactOptionalPropertyTypes.
  const pickScoring = (src: Partial<Lead>): Partial<Lead> => {
    const out: Partial<Lead> = {};
    if (src.score !== undefined) out.score = src.score;
    if (src.priority !== undefined) out.priority = src.priority;
    if (src.status !== undefined) out.status = src.status;
    return out;
  };

  // Score/priority are recomputed by the backend's batched rescore (~1.5-3 s after a write), so a
  // row edited in place keeps its old score until we ask again. Pull the fresh values once
  // that window has passed. Scoring fields only — never clobbers an in-flight status edit.
  const refreshLeadScoring = (leadId: string) => {
    // Two looks (the flush can be slower on a cold connection); each is a single cheap GET.
    for (const delayMs of [2500, 5500]) {
      window.setTimeout(() => {
        adminApi
          .getLeadById(leadId)
          .then((fresh) => {
            if (!fresh?._id) return;
            setLeads((prev) =>
              prev.map((l) => {
                if (l._id !== leadId) return l;
                const next: Lead = { ...l };
                if (fresh.score !== undefined) next.score = fresh.score;
                if (fresh.priority !== undefined) next.priority = fresh.priority;
                return next;
              })
            );
          })
          .catch(() => {});
      }, delayMs);
    }
  };

  const openLeadDetail = (lead: Lead) => {
    setDetailLead(lead);
    fetchEnquiryHistory(lead._id);
    // The row we were handed can be stale (score/priority are rewritten in the background
    // by the rescore queue) — overlay the server's current values so the popup never
    // disagrees with the list.
    adminApi
      .getLeadById(lead._id)
      .then((fresh) => {
        if (!fresh?._id) return;
        setDetailLead((d) =>
          d && d._id === fresh._id
            ? { ...d, ...pickScoring(fresh) }
            : d
        );
      })
      .catch(() => {});
  };

  // Keep an open popup in step with the list behind it (status change, re-score, refetch).
  useEffect(() => {
    setDetailLead((d) => {
      if (!d) return d;
      const live = leads.find((l) => l._id === d._id);
      return live && (live.score !== d.score || live.priority !== d.priority || live.status !== d.status)
        ? { ...d, ...pickScoring(live) }
        : d;
    });
  }, [leads]);

  // ?lead=<id> deep link — fetch that one lead directly (independent of the list's
  // status/search filters, so it works even for a non-"new" lead), open its popup,
  // AND narrow the list behind it to that lead (search = its phone/email/name,
  // status = all) so closing the popup lands on the lead in context rather than on
  // the generic "new" list. The param is dropped in the same patch, so refreshing
  // doesn't reopen the popup.
  useEffect(() => {
    const id = query.lead;
    if (!id) return;
    let cancelled = false;
    adminApi
      .getLeadById(id)
      .then((lead) => {
        // Cancelled = unmounted / navigated away / superseded: touch NOTHING, in particular
        // not the URL (patchQuery would rewrite whatever page the user has moved on to).
        if (cancelled) return;
        if (!lead?._id) {
          patchQuery({ lead: '' });
          return;
        }
        const term: string =
          lead.customerPhone || lead.customerEmail || lead.customerName || '';
        setSearch(term);
        patchQuery({ lead: '', search: term, status: 'all', page: 1 });
        openLeadDetail(lead as Lead);
      })
      .catch(() => {
        /* unknown / out-of-scope lead id — just land on the normal list */
        if (!cancelled) patchQuery({ lead: '' });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.lead]);

  /**
   * A lead row points at three different places, so each gets its own affordance
   * rather than overloading one click (mirrors Contacts.tsx, which established
   * this split):
   *   - the WhatsApp conversation  → chat icon
   *   - the contact CRM record     → the name (a link, like Contacts')
   *   - this lead's own detail     → the eye icon / row menu
   *
   * Deep-linked by PHONE, not by id: a Lead `_id` is not a Conversation id and
   * not a Contact id, so passing it as `conversationId` (as Contacts does with
   * its own id) would silently select the wrong chat or none at all. ChatLogs
   * matches `?phone=` against the conversation list — see its deep-link effect.
   */
  const openLeadChat = (lead: Lead) => {
    if (!lead.customerPhone) return;
    const base = location.pathname.startsWith('/admin')
      ? '/admin/activity/chat-logs'
      : '/activity/chat-logs';
    navigate(`${base}?phone=${encodeURIComponent(lead.customerPhone)}`);
  };

  /**
   * The Contact record for this lead's phone.
   *
   * Resolved by LOOKUP, not by id arithmetic: Leads and Contacts are separate
   * collections with no FK between them, and a Contact row is really a
   * Conversation (`/admin/contacts/:id` takes the conversation id — see
   * Contacts.tsx, which lists via `getConversations`). So the phone is searched
   * and the resulting id used; guessing would route to the wrong record or 404.
   */
  const openContactForLead = async (lead: Lead) => {
    if (!lead.customerPhone) return;
    const prefix = location.pathname.startsWith('/admin') ? '/admin' : '';
    setContactLookupLeadId(lead._id);
    try {
      const res = await adminApi.getConversations({ search: lead.customerPhone, limit: 1 });
      const match = ((res.data as any[]) || [])[0];
      if (match?._id) {
        navigate(`${prefix}/contacts/${match._id}`);
      } else {
        setLeadNavError('No contact record exists for this number yet.');
      }
    } catch {
      setLeadNavError('Could not open the contact record.');
    } finally {
      setContactLookupLeadId(null);
    }
  };

  const toggleExpandLead = (leadId: string) => {
    setExpandedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
        return next;
      }
      next.add(leadId);
      // Refetch page 1 on every expand, not just the first — a lead this row is showing
      // can pick up new enquiries (a live WhatsApp AI conversation, a re-submission) while
      // the row is collapsed or was expanded earlier in the session, and the one-time-only
      // cache this used to be left a stale, incomplete history showing forever until a
      // full page reload. `enquiryHistory`/`enquiryHistoryTotal` still double as the
      // "load more" pagination state between refetches.
      fetchEnquiryHistory(leadId);
      return next;
    });
  };

  const loadMoreEnquiryHistory = (leadId: string) => {
    const alreadyLoaded = enquiryHistory[leadId]?.length || 0;
    const nextPage = Math.floor(alreadyLoaded / 20) + 1;
    setEnquiryLoading((p) => ({ ...p, [leadId]: true }));
    adminApi
      .getLeadEnquiries(leadId, { page: nextPage, limit: 20 })
      .then((res) => {
        setEnquiryHistory((p) => ({ ...p, [leadId]: [...(p[leadId] || []), ...(res.data || [])] }));
        setEnquiryHistoryTotal((p) => ({ ...p, [leadId]: res.pagination?.total ?? alreadyLoaded }));
      })
      .catch((err) => console.error('Error loading more enquiry history:', err))
      .finally(() => setEnquiryLoading((p) => ({ ...p, [leadId]: false })));
  };

  const handleStatusChange = async (leadId: string, newStatus: string) => {
    const previous = leads.find((l) => l._id === leadId)?.status;
    if (previous === newStatus) return;
    // Optimistic — reverted below if the request fails.
    setLeads((prev) =>
      prev.map((l) => (l._id === leadId ? { ...l, status: newStatus as Lead['status'] } : l))
    );
    setStatusUpdating((p) => ({ ...p, [leadId]: true }));
    try {
      await adminApi.updateLead(leadId, { status: newStatus });
      fetchLeadStats();
      refreshLeadScoring(leadId);
    } catch (error) {
      console.error('Error updating status:', error);
      setLeads((prev) =>
        prev.map((l) => (l._id === leadId ? { ...l, status: previous as Lead['status'] } : l))
      );
      alert('Failed to update status. Please try again.');
    } finally {
      setStatusUpdating((p) => ({ ...p, [leadId]: false }));
    }
  };


  const toggleSelectLead = (leadId: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedLeadIds((prev) =>
      prev.size === leads.length ? new Set() : new Set(leads.map((l) => l._id))
    );
  };

  const handleBulkStatusChange = async () => {
    if (!bulkStatusValue || selectedLeadIds.size === 0) return;
    setBulkActionLoading(true);
    try {
      await adminApi.bulkUpdateLeadStatus(Array.from(selectedLeadIds), bulkStatusValue);
      setSelectedLeadIds(new Set());
      setBulkStatusValue('');
      fetchLeads();
      fetchLeadStats();
    } catch (error) {
      console.error('Error bulk-updating status:', error);
      alert('Failed to update status for the selected leads.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeadIds.size === 0) return;
    const confirmed = window.confirm(
      `Delete ${selectedLeadIds.size} selected ${selectedLeadIds.size === 1 ? leadWord.singular.toLowerCase() : leadWord.plural.toLowerCase()} permanently?`
    );
    if (!confirmed) return;
    setBulkActionLoading(true);
    try {
      await adminApi.bulkDeleteLeads(Array.from(selectedLeadIds));
      setSelectedLeadIds(new Set());
      fetchLeads();
      fetchLeadStats();
    } catch (error) {
      console.error('Error bulk-deleting leads:', error);
      alert('Failed to delete the selected leads.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkAssignLeads = async () => {
    if (!bulkLeadAssignUserId || selectedLeadIds.size === 0) return;
    setBulkLeadAssignSaving(true);
    setBulkLeadAssignError('');
    try {
      const result = await adminApi.bulkAssignLeads(Array.from(selectedLeadIds), bulkLeadAssignUserId);
      setSelectedLeadIds(new Set());
      setBulkLeadAssignOpen(false);
      setBulkLeadAssignUserId('');
      const msg = `Assigned ${result.assigned} ${result.assigned === 1 ? leadWord.singular.toLowerCase() : leadWord.plural.toLowerCase()}`;
      setBulkLeadSuccessMsg(
        result.failed?.length ? `${msg} (${result.failed.length} could not be assigned)` : msg
      );
      fetchLeads();
      fetchLeadStats();
    } catch (e: any) {
      setBulkLeadAssignError(e?.response?.data?.error?.message || e.message || 'Bulk assign failed');
    } finally {
      setBulkLeadAssignSaving(false);
    }
  };

  const memberName = (m: any) => `${m?.firstName || ''} ${m?.lastName || ''}`.trim() || m?.email || 'Unknown';

  /** Assign / reassign / unassign from the row, optimistically — a refetch rolls back on failure. */
  const assignInline = async (lead: Lead, userId: string | null) => {
    setAssignMenu(null);
    const advance = userId !== null && lead.status === 'new'; // server moves a NEW lead to contacted
    setLeads((prev) =>
      prev.map((l) => {
        if (l._id !== lead._id) return l;
        const { assignedTo: _old, ...rest } = l;
        return {
          ...rest,
          ...(userId ? { assignedTo: userId } : {}),
          ...(advance ? { status: 'contacted' as Lead['status'] } : {}),
        };
      })
    );
    try {
      await adminApi.assignLead(lead._id, userId);
      setBulkLeadSuccessMsg(
        userId ? `Assigned to ${memberName(teamMembers.find((m) => m._id === userId))}` : 'Unassigned'
      );
      fetchLeadStats();
    } catch (e: any) {
      setActionErrorMsg(e?.response?.data?.error?.message || e?.message || 'Could not update the assignee');
      fetchLeads();
    }
  };

  const handleQuickAssignLead = async () => {
    if (!quickAssignLead || !quickAssignLeadUserId) return;
    setQuickAssignLeadSaving(true);
    setQuickAssignLeadError('');
    try {
      await adminApi.assignLead(quickAssignLead._id, quickAssignLeadUserId);
      setQuickAssignLead(null);
      setQuickAssignLeadUserId('');
      setBulkLeadSuccessMsg(`Assigned ${quickAssignLead.customerName || 'lead'}`);
      fetchLeads();
      fetchLeadStats();
    } catch (e: any) {
      setQuickAssignLeadError(e?.response?.data?.error?.message || e.message || 'Failed to assign');
    } finally {
      setQuickAssignLeadSaving(false);
    }
  };

  // Leads currently selected via the bulk checkboxes, oldest first — feeds both the merge
  // dialog's default pick (oldest survives) and its list of radio options.
  const selectedLeadsForMerge = useMemo(
    () =>
      leads
        .filter((l) => selectedLeadIds.has(l._id))
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
    [leads, selectedLeadIds]
  );

  const openMergeDialog = () => {
    setMergeError('');
    setMergePrimaryId(selectedLeadsForMerge[0]?._id || '');
    setMergeDialogOpen(true);
  };

  const handleMergeLeads = async () => {
    if (!mergePrimaryId) return;
    const duplicateIds = Array.from(selectedLeadIds).filter((id) => id !== mergePrimaryId);
    if (duplicateIds.length === 0) return;
    setMerging(true);
    setMergeError('');
    try {
      await adminApi.mergeLeads(mergePrimaryId, duplicateIds);
      setMergeDialogOpen(false);
      setSelectedLeadIds(new Set());
      fetchLeads();
      fetchLeadStats();
    } catch (e: any) {
      setMergeError(e?.response?.data?.error || 'Failed to merge leads. Please try again.');
      console.error('Error merging leads:', e);
    } finally {
      setMerging(false);
    }
  };

  // Stats — real tenant-wide totals from getLeadStats, not the current (paginated,
  // possibly filtered) page of `leads`.
  const totalLeads = leadStats?.total ?? 0;
  const newLeads = leadStats?.byStatus.new ?? 0;
  const qualifiedLeads = leadStats?.byStatus.qualified ?? 0;
  const convertedLeads = leadStats?.byStatus.converted ?? 0;

  return (
    <Box>
      <PageHeader
        title={leadWord.plural}
        subtitle={`Track and manage your ${leadWord.plural.toLowerCase()}`}
        actions={
          <>
            <Tooltip title="Table columns">
              <IconButton size="small" onClick={openColumnsDialog} aria-label="Table columns">
                <ViewColumn />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFile />}
              onClick={openImportLeads}
            >
              Import
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => setAddLeadDialogOpen(true)}
            >
              Add {leadWord.singular}
            </Button>
          </>
        }
      />

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: totalLeads, color: '#4f46e5' },
          { label: 'New', value: newLeads, color: '#3b82f6' },
          { label: 'Qualified', value: qualifiedLeads, color: '#10b981' },
          { label: 'Converted', value: convertedLeads, color: '#f59e0b' },
        ].map(({ label, value, color }) => (
          <Grid item xs={6} sm={3} key={label}>
            <Card>
              <CardContent sx={{ py: '14px !important' }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600 }}
                >
                  {label}
                </Typography>
                {leadStats === null ? (
                  <Skeleton width={48} height={32} />
                ) : (
                  <Typography variant="h4" sx={{ fontWeight: 700, color, lineHeight: 1.1 }}>
                    {value}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Bulk action bar — only shown once at least one row is selected */}
      {selectedLeadIds.size > 0 && (
        <Card sx={{ mb: 2, bgcolor: alpha('#4f46e5', 0.06) }}>
          <CardContent sx={{ py: '10px !important' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
                {selectedLeadIds.size} selected
              </Typography>
              <TextField
                select
                size="small"
                label="Set status to…"
                value={bulkStatusValue}
                onChange={(e) => setBulkStatusValue(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                {LEAD_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                size="small"
                disabled={!bulkStatusValue || bulkActionLoading}
                onClick={handleBulkStatusChange}
              >
                Apply
              </Button>
              {selectedLeadIds.size >= 2 && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={bulkActionLoading}
                  onClick={openMergeDialog}
                >
                  Merge
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AssignmentInd />}
                disabled={bulkActionLoading}
                onClick={() => { setBulkLeadAssignError(''); setBulkLeadAssignOpen(true); }}
              >
                Assign to…
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteOutline />}
                disabled={bulkActionLoading}
                onClick={handleBulkDelete}
                sx={{ ml: 'auto' }}
              >
                Delete selected
              </Button>
              <Button
                size="small"
                onClick={() => setSelectedLeadIds(new Set())}
                disabled={bulkActionLoading}
              >
                Clear
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ py: '14px !important' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Search leads…"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={2.5}>
              <TextField
                fullWidth
                select
                size="small"
                label="Status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                {LEAD_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2.5}>
              <TextField
                fullWidth
                select
                size="small"
                label="Priority"
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
              >
                <MenuItem value="all">All Priorities</MenuItem>
                {['hot', 'high', 'medium', 'low'].map((p) => (
                  <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2.5}>
              <TextField
                fullWidth
                select
                size="small"
                label="Time"
                value={filterDateRange}
                onChange={(e) => setFilterDateRange(e.target.value)}
              >
                {DATE_RANGE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Autocomplete
                multiple
                size="small"
                options={allTags}
                value={filterTags}
                onChange={(_, value) => setFilterTags(value)}
                renderTags={(value, getTagProps) =>
                  value.map((tag, index) => (
                    <Chip label={tag} size="small" {...getTagProps({ index })} key={tag} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...(params as any)}
                    label="Tags"
                    placeholder={filterTags.length ? '' : 'Filter by tag…'}
                  />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent sx={{ p: '0 !important' }}>
          {loading ? (
            <Box sx={{ p: 3 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                  <Skeleton variant="circular" width={36} height={36} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton height={16} width="35%" />
                    <Skeleton height={14} width="55%" />
                  </Box>
                  <Skeleton width={70} height={24} />
                  <Skeleton width={70} height={24} />
                  <Skeleton width={36} height={36} />
                </Box>
              ))}
            </Box>
          ) : leads.length === 0 ? (
            <EmptyState
              icon={<PersonAdd />}
              title={`No ${leadWord.plural.toLowerCase()} yet`}
              description={`Add ${leadWord.plural.toLowerCase()} manually or import from a CSV file to get started`}
              action={{
                label: `Add ${leadWord.singular}`,
                onClick: () => setAddLeadDialogOpen(true),
                startIcon: <Add />,
              }}
            />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedLeadIds.size > 0 && selectedLeadIds.size < leads.length
                        }
                        checked={leads.length > 0 && selectedLeadIds.size === leads.length}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell padding="checkbox" />
                    <TableCell>Contact</TableCell>
                    {visibleColumns.enquiry && <TableCell>Enquiry</TableCell>}
                    {visibleColumns.tags && <TableCell>Tags</TableCell>}
                    {visibleColumns.source && <TableCell>Source</TableCell>}
                    {visibleColumns.lastActivity && <TableCell>Last Activity</TableCell>}
                    {visibleColumns.status && <TableCell>Status</TableCell>}
                    {visibleColumns.priority && <TableCell>Priority</TableCell>}
                    {visibleColumns.score && (
                      <TableCell sortDirection={sortByScore ? 'desc' : false}>
                        <TableSortLabel
                          active={sortByScore}
                          direction="desc"
                          onClick={() => {
                            setPage(1);
                            setSortByScore((v) => !v);
                          }}
                        >
                          Score
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {visibleColumns.assignedTo && <TableCell>Assigned To</TableCell>}
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leads.map((lead) => {
                    const isExpanded = expandedLeadIds.has(lead._id);
                    const assignee = teamMembers.find((m) => m._id === lead.assignedTo);
                    const hasDetail = Boolean(
                      lead.description ||
                      lead.notes ||
                      lead.interest ||
                      lead.budget ||
                      lead.estimatedValue !== undefined ||
                      (lead.customAttributes && lead.customAttributes.length > 0) ||
                      lead.sourceDetail
                    );
                    return (
                      <Fragment key={lead._id}>
                        <TableRow hover selected={selectedLeadIds.has(lead._id)}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedLeadIds.has(lead._id)}
                              onChange={() => toggleSelectLead(lead._id)}
                            />
                          </TableCell>
                          <TableCell padding="checkbox">
                            <IconButton
                              size="small"
                              onClick={() => toggleExpandLead(lead._id)}
                              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                            >
                              {isExpanded ? (
                                <KeyboardArrowUp sx={{ fontSize: 18 }} />
                              ) : (
                                <KeyboardArrowDown sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                sx={{
                                  width: 34,
                                  height: 34,
                                  bgcolor: alpha('#4f46e5', 0.1),
                                  color: '#4f46e5',
                                  fontSize: '0.8125rem',
                                  fontWeight: 700,
                                }}
                              >
                                {(
                                  lead.customerName?.[0] ||
                                  lead.customerPhone?.[0] ||
                                  lead.customerEmail?.[0] ||
                                  '?'
                                ).toUpperCase()}
                              </Avatar>
                              <Box>
                                {/* The name opens the CONTACT record — same meaning
                                    it has on the Contacts page, so the two lists
                                    stay consistent. This lead's own detail lives on
                                    the eye icon in Actions. */}
                                <Tooltip
                                  title={
                                    lead.customerPhone
                                      ? 'Open contact record'
                                      : 'No phone number on this lead'
                                  }
                                >
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    fontWeight={600}
                                    onClick={() =>
                                      contactLookupLeadId ? undefined : openContactForLead(lead)
                                    }
                                    sx={{
                                      // `block`, not `inline-block`: the parent Box
                                      // stacks name over phone, and an inline span
                                      // let the phone ride up onto the same line.
                                      display: 'block',
                                      cursor: lead.customerPhone ? 'pointer' : 'default',
                                      ...(lead.customerPhone
                                        ? {
                                            color: 'primary.main',
                                            '&:hover': { textDecoration: 'underline' },
                                          }
                                        : {}),
                                    }}
                                  >
                                    {lead.customerName || 'Unknown'}
                                  </Typography>
                                </Tooltip>
                                <Typography variant="caption" color="text.secondary">
                                  {lead.customerPhone
                                    ? formatPhoneDisplay(lead.customerPhone)
                                    : lead.customerEmail || '—'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          {visibleColumns.enquiry && (
                          <TableCell>
                            {lead.title ? (
                              <Tooltip
                                title={lead.description || ''}
                                disableHoverListener={!lead.description}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {lead.title}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          )}
                          {visibleColumns.tags && (
                          <TableCell>
                            {lead.tags && lead.tags.length > 0 ? (
                              <Box
                                sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 180 }}
                              >
                                {lead.tags.slice(0, 2).map((tag) => (
                                  <Chip key={tag} label={tag} size="small" />
                                ))}
                                {lead.tags.length > 2 && (
                                  <Chip
                                    label={`+${lead.tags.length - 2}`}
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTagsAnchorEl(e.currentTarget);
                                      setTagsPopoverLeadId(lead._id);
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                  />
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          )}
                          {visibleColumns.source && (
                          <TableCell>
                            <Tooltip
                              title={lead.sourceDetail ? `Channel: ${lead.source}` : ''}
                              disableHoverListener={!lead.sourceDetail}
                            >
                              <span>
                                <SourceChip
                                  source={lead.source}
                                  {...(lead.sourceDetail ? { label: lead.sourceDetail } : {})}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                          )}
                          {visibleColumns.lastActivity && (
                          <TableCell>
                            {(() => {
                              // Both are optional on the wire (lastContactAt is optional on
                              // the schema; createdAt can be absent on older/imported rows),
                              // and timeAgo returns '' for an unparseable date — without this
                              // guard the cell renders blank under an "Invalid Date" tooltip.
                              const when = lead.lastContactAt || lead.createdAt;
                              const label = when ? timeAgo(when) : '';
                              if (!label) {
                                return (
                                  <Typography variant="body2" color="text.disabled">
                                    —
                                  </Typography>
                                );
                              }
                              return (
                                <Tooltip title={new Date(when).toLocaleString()}>
                                  <Typography variant="body2" color="text.secondary">
                                    {label}
                                  </Typography>
                                </Tooltip>
                              );
                            })()}
                          </TableCell>
                          )}
                          {visibleColumns.status && (
                          <TableCell>
                            <Select
                              size="small"
                              variant="standard"
                              disableUnderline
                              value={lead.status}
                              disabled={Boolean(statusUpdating[lead._id])}
                              onChange={(e) => handleStatusChange(lead._id, e.target.value)}
                              renderValue={(value) => <StatusChip status={value as string} />}
                              sx={{ '.MuiSelect-select': { py: 0, display: 'flex' } }}
                            >
                              {LEAD_STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                                  {s}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          )}
                          {visibleColumns.priority && (
                          <TableCell>
                            {lead.priority ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: priorityColor[lead.priority] ?? '#94a3b8',
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  sx={{ textTransform: 'capitalize' }}
                                >
                                  {lead.priority}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          )}
                          {visibleColumns.score && (
                          <TableCell>
                            {lead.score !== undefined ? (
                              <Tooltip title="Why this score?">
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  onClick={(e) => openWhy(e.currentTarget, lead._id)}
                                  sx={{ cursor: 'pointer', textDecoration: 'underline dotted', width: 'fit-content' }}
                                >
                                  {lead.score}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          )}
                          {visibleColumns.assignedTo && (
                          <TableCell>
                            {(() => {
                              const heldByOther = Boolean(lead.assignedTo) && lead.assignedTo !== wsUser?.id;
                              const locked = heldByOther && !canReassignOthers;
                              const name = assignee ? memberName(assignee) : '';
                              return (
                                <Tooltip
                                  title={
                                    locked
                                      ? 'Reassigning a lead held by someone else needs Full access to Leads'
                                      : assignee
                                        ? 'Change assignee'
                                        : 'Assign to a team member'
                                  }
                                >
                                  <span>
                                    <ButtonBase
                                      disabled={locked}
                                      onClick={(e) => setAssignMenu({ anchor: e.currentTarget, lead })}
                                      aria-label={assignee ? `Assigned to ${name}. Change` : 'Assign lead'}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 0.75,
                                        py: 0.5,
                                        ml: -0.75,
                                        borderRadius: 1,
                                        border: '1px solid transparent',
                                        '&:hover:not(.Mui-disabled)': { bgcolor: 'action.hover', borderColor: 'divider' },
                                      }}
                                    >
                                      {assignee ? (
                                        <>
                                          <Avatar
                                            sx={{
                                              width: 22,
                                              height: 22,
                                              fontSize: 11,
                                              fontWeight: 700,
                                              bgcolor: alpha('#4f46e5', 0.12),
                                              color: '#4f46e5',
                                            }}
                                          >
                                            {name[0]?.toUpperCase()}
                                          </Avatar>
                                          <Typography variant="body2">{name}</Typography>
                                        </>
                                      ) : (
                                        <>
                                          <AssignmentInd sx={{ fontSize: 16, color: 'text.disabled' }} />
                                          <Typography variant="body2" color="text.secondary">
                                            Assign
                                          </Typography>
                                        </>
                                      )}
                                      {!locked && <KeyboardArrowDown sx={{ fontSize: 16, color: 'text.disabled' }} />}
                                    </ButtonBase>
                                  </span>
                                </Tooltip>
                              );
                            })()}
                          </TableCell>
                          )}
                          <TableCell align="right" sx={{ width: 56 }}>
                            <Tooltip title="Actions">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  setAnchorEl(e.currentTarget);
                                  setSelectedLeadId(lead._id);
                                }}
                                aria-label="Lead actions"
                              >
                                <MoreVert sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            colSpan={visibleColumnCount}
                            sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}
                          >
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 2 }}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                      <Typography variant="subtitle2">Enquiry details</Typography>
                                      <Button
                                        size="small"
                                        startIcon={<Visibility sx={{ fontSize: 16 }} />}
                                        onClick={() => openLeadDetail(lead)}
                                        sx={{ borderRadius: 2, textTransform: 'none' }}
                                      >
                                        View details
                                      </Button>
                                    </Box>
                                    {!hasDetail ? (
                                      <Typography variant="body2" color="text.disabled">
                                        No additional details for this{' '}
                                        {leadWord.singular.toLowerCase()}.
                                      </Typography>
                                    ) : (
                                      <Stack spacing={1}>
                                        {lead.description && (
                                          <Box>
                                            <Typography variant="caption" color="text.secondary">
                                              Description
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              sx={{ overflowWrap: 'break-word' }}
                                            >
                                              {lead.description}
                                            </Typography>
                                          </Box>
                                        )}
                                        {lead.interest && (
                                          <Box>
                                            <Typography variant="caption" color="text.secondary">
                                              Interest
                                            </Typography>
                                            <Typography variant="body2">{lead.interest}</Typography>
                                          </Box>
                                        )}
                                        {(lead.budget || lead.estimatedValue !== undefined) && (
                                          <Box>
                                            <Typography variant="caption" color="text.secondary">
                                              Budget / Value
                                            </Typography>
                                            <Typography variant="body2">
                                              {[
                                                lead.budget,
                                                lead.estimatedValue !== undefined
                                                  ? String(lead.estimatedValue)
                                                  : '',
                                              ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                            </Typography>
                                          </Box>
                                        )}
                                        {lead.notes && (
                                          <Box>
                                            <Typography variant="caption" color="text.secondary">
                                              Notes
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              sx={{ overflowWrap: 'break-word' }}
                                            >
                                              {lead.notes}
                                            </Typography>
                                          </Box>
                                        )}
                                        {lead.sourceDetail && (
                                          <Box>
                                            <Typography variant="caption" color="text.secondary">
                                              Source detail
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              sx={{ overflowWrap: 'break-word' }}
                                            >
                                              {lead.sourceDetail}
                                            </Typography>
                                          </Box>
                                        )}
                                        {lead.customAttributes && lead.customAttributes.length > 0 && (
                                          <Box>
                                            <Typography variant="caption" color="text.secondary">
                                              Custom fields
                                            </Typography>
                                            <CustomFieldValues
                                              attributes={lead.customAttributes}
                                              definitions={leadFieldDefs}
                                              dense
                                            />
                                          </Box>
                                        )}
                                      </Stack>
                                    )}
                                  </Grid>
                                  <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                      Enquiry history
                                    </Typography>
                                    {enquiryLoading[lead._id] && !enquiryHistory[lead._id] ? (
                                      <CircularProgress size={20} />
                                    ) : !enquiryHistory[lead._id] ||
                                      enquiryHistory[lead._id]?.length === 0 ? (
                                      <Typography variant="body2" color="text.disabled">
                                        No recorded submissions.
                                      </Typography>
                                    ) : (
                                      <Stack spacing={1} sx={{ maxHeight: 260, overflowY: 'auto' }}>
                                        {enquiryHistory[lead._id]?.map((enq) => (
                                          <Box
                                            key={enq._id}
                                            sx={{
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              borderRadius: 1,
                                              p: 1,
                                            }}
                                          >
                                            <Stack
                                              direction="row"
                                              spacing={1}
                                              alignItems="center"
                                              sx={{ mb: 0.5 }}
                                            >
                                              <IngestionChip path={enq.ingestionPath} />
                                              <Tooltip
                                                title={new Date(enq.createdAt).toLocaleString()}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  {timeAgo(enq.createdAt)}
                                                </Typography>
                                              </Tooltip>
                                            </Stack>
                                            {(enq.title || enq.description) && (
                                              <Typography variant="body2">
                                                {enq.title}
                                                {enq.title && enq.description ? ' — ' : ''}
                                                {enq.description}
                                              </Typography>
                                            )}
                                          </Box>
                                        ))}
                                        {(enquiryHistoryTotal[lead._id] ?? 0) >
                                          (enquiryHistory[lead._id]?.length ?? 0) && (
                                          <Button
                                            size="small"
                                            onClick={() => loadMoreEnquiryHistory(lead._id)}
                                            disabled={Boolean(enquiryLoading[lead._id])}
                                            sx={{ alignSelf: 'flex-start' }}
                                          >
                                            {enquiryLoading[lead._id] ? (
                                              <CircularProgress
                                                size={14}
                                                sx={{ color: 'inherit' }}
                                              />
                                            ) : (
                                              'Load more'
                                            )}
                                          </Button>
                                        )}
                                      </Stack>
                                    )}
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {!loading && totalPages > 1 && (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mt: 2.5 }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="small"
          />
          <Typography variant="caption" color="text.secondary">
            {totalCount}{' '}
            {totalCount === 1 ? leadWord.singular.toLowerCase() : leadWord.plural.toLowerCase()}{' '}
            total
          </Typography>
        </Box>
      )}

      {/* Row actions — the ONLY per-row control; everything a row can do lives here. */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { minWidth: 200, borderRadius: 2 } }}
      >
        {(() => {
          const lead = leads.find((l) => l._id === selectedLeadId) || null;
          const hasPhone = Boolean(lead?.customerPhone);
          return [
            <MenuItem
              key="view"
              onClick={() => {
                setAnchorEl(null);
                if (lead) openLeadDetail(lead);
              }}
            >
              <Visibility sx={{ mr: 1.5, fontSize: 18 }} /> View details
            </MenuItem>,
            <MenuItem
              key="chat"
              disabled={!hasPhone}
              onClick={() => {
                setAnchorEl(null);
                if (lead) openLeadChat(lead);
              }}
            >
              <ChatBubbleOutline sx={{ mr: 1.5, fontSize: 18 }} />
              {hasPhone ? 'Open chat' : 'Open chat (no phone)'}
            </MenuItem>,
            <MenuItem
              key="assign"
              onClick={() => {
                setQuickAssignLead(lead);
                setQuickAssignLeadUserId('');
                setQuickAssignLeadError('');
                setAnchorEl(null);
              }}
            >
              <AssignmentInd sx={{ mr: 1.5, fontSize: 18 }} /> Assign…
            </MenuItem>,
            <Divider key="div" />,
            <MenuItem key="delete" onClick={handleDeleteLead} sx={{ color: 'error.main' }}>
              <DeleteOutline sx={{ mr: 1.5, fontSize: 18 }} /> Delete {leadWord.singular.toLowerCase()}
            </MenuItem>,
          ];
        })()}
      </Menu>

      {/* Table columns. Rendered from LEAD_COLUMNS rather than a hand-written
          list of checkboxes, so adding a column is a one-line change and the
          dialog can never drift out of sync with the table. */}
      <Dialog open={columnsOpen} onClose={() => setColumnsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Table Columns</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary">
            Contact and Actions are always shown.
          </Typography>
          <FormGroup sx={{ mt: 1 }}>
            {LEAD_COLUMNS.map((col) => (
              <FormControlLabel
                key={col.key}
                control={
                  <Checkbox
                    checked={columnDraft[col.key]}
                    onChange={(e) =>
                      setColumnDraft((prev) => ({ ...prev, [col.key]: e.target.checked }))
                    }
                  />
                }
                label={col.label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnsOpen(false)}>Cancel</Button>
          <Button onClick={saveColumns} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* All tags for a row's "+N" overflow chip */}
      <Popover
        open={Boolean(tagsAnchorEl)}
        anchorEl={tagsAnchorEl}
        onClose={() => {
          setTagsAnchorEl(null);
          setTagsPopoverLeadId(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 280 }}>
          {(() => {
            // The row can disappear from `leads` while this is open (a refetch, a
            // filter change, paging) — render a placeholder rather than an empty box.
            const tags = leads.find((l) => l._id === tagsPopoverLeadId)?.tags || [];
            if (!tags.length) {
              return (
                <Typography variant="body2" color="text.disabled">
                  No tags to show.
                </Typography>
              );
            }
            return tags.map((tag) => <Chip key={tag} label={tag} size="small" />);
          })()}
        </Box>
      </Popover>

      {/* Full lead detail — everything the row expander summarises, plus the
          per-submission fields that had no home in the UI at all. */}
      <Dialog
        open={Boolean(detailLead)}
        onClose={() => setDetailLead(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        {detailLead && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: alpha('#4f46e5', 0.1),
                    color: '#4f46e5',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                  }}
                >
                  {(
                    detailLead.customerName?.[0] ||
                    detailLead.customerPhone?.[0] ||
                    detailLead.customerEmail?.[0] ||
                    '?'
                  ).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    {detailLead.customerName || 'Unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {detailLead.customerPhone
                      ? formatPhoneDisplay(detailLead.customerPhone)
                      : detailLead.customerEmail || '—'}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setDetailLead(null)} aria-label="Close">
                  <Close sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, pt: 0.5 }}>
                  <StatusChip status={detailLead.status} />
                  {detailLead.priority && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: priorityColor[detailLead.priority] ?? '#94a3b8',
                        }}
                      />
                      <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                        {detailLead.priority}
                      </Typography>
                    </Box>
                  )}
                  {detailLead.score !== undefined && (
                    <Typography variant="body2" color="text.secondary">
                      Score <b style={{ color: 'inherit' }}>{detailLead.score}</b>
                    </Typography>
                  )}
                  <SourceChip
                    source={detailLead.source}
                    {...(detailLead.sourceDetail ? { label: detailLead.sourceDetail } : {})}
                  />
                </Box>

                <DetailSection title="Contact">
                  <DetailField label="Name" value={detailLead.customerName} />
                  <DetailField
                    label="Phone"
                    value={
                      detailLead.customerPhone
                        ? formatPhoneDisplay(detailLead.customerPhone)
                        : undefined
                    }
                  />
                  <DetailField label="Email" value={detailLead.customerEmail} />
                  <DetailField label="Company" value={detailLead.customerCompany} />
                  <DetailField label="Country" value={detailLead.customerCountry} />
                </DetailSection>

                <DetailSection title="Current enquiry">
                  <DetailField label="Title" value={detailLead.title} />
                  <DetailField label="Interest" value={detailLead.interest} />
                  <DetailField label="Description" value={detailLead.description} />
                  <DetailField label="Budget" value={detailLead.budget} />
                  <DetailField
                    label="Estimated value"
                    value={
                      detailLead.estimatedValue !== undefined
                        ? String(detailLead.estimatedValue)
                        : undefined
                    }
                  />
                  <DetailField label="Notes" value={detailLead.notes} />
                </DetailSection>

                {detailLead.tags && detailLead.tags.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                      Tags
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {detailLead.tags.map((t) => (
                        <Chip key={t} size="small" label={t} />
                      ))}
                    </Box>
                  </Box>
                )}

                {detailLead.customAttributes && detailLead.customAttributes.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
                      Custom fields
                    </Typography>
                    <CustomFieldValues
                      attributes={detailLead.customAttributes}
                      definitions={leadFieldDefs}
                    />
                  </Box>
                )}

                <DetailSection title="Tracking">
                  <DetailField
                    label="Assigned to"
                    value={(() => {
                      const m = teamMembers.find((x) => x._id === detailLead.assignedTo);
                      return m
                        ? `${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email
                        : undefined;
                    })()}
                  />
                  <DetailField
                    label="Last activity"
                    value={
                      detailLead.lastContactAt
                        ? new Date(detailLead.lastContactAt).toLocaleString()
                        : undefined
                    }
                  />
                  <DetailField
                    label="Created"
                    value={
                      detailLead.createdAt
                        ? new Date(detailLead.createdAt).toLocaleString()
                        : undefined
                    }
                  />
                </DetailSection>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Enquiry history
                    {enquiryHistoryTotal[detailLead._id] !== undefined &&
                      ` (${enquiryHistoryTotal[detailLead._id]})`}
                  </Typography>
                  {enquiryLoading[detailLead._id] && !enquiryHistory[detailLead._id] ? (
                    <CircularProgress size={20} />
                  ) : !enquiryHistory[detailLead._id]?.length ? (
                    <Typography variant="body2" color="text.disabled">
                      No recorded submissions.
                    </Typography>
                  ) : (
                    <Stack spacing={1.25}>
                      {enquiryHistory[detailLead._id]?.map((enq) => (
                        <Box
                          key={enq._id}
                          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{ mb: 0.75, gap: 0.5 }}
                          >
                            <IngestionChip path={enq.ingestionPath} />
                            {/* The source chip only adds information when it differs from the path. */}
                            {enq.source && sourceLabel(enq.source) !== ingestionLabel(enq.ingestionPath) && (
                              <SourceChip source={enq.source} />
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {new Date(enq.createdAt).toLocaleString()}
                            </Typography>
                          </Stack>
                          {/* These per-submission fields are the whole reason this
                              panel exists — the inline expander shows only
                              title/description, so an older enquiry's own interest,
                              budget, notes and tags were invisible. */}
                          <Stack spacing={0.5}>
                            <DetailField label="Title" value={enq.title} dense />
                            <DetailField label="Interest" value={enq.interest} dense />
                            <DetailField label="Description" value={enq.description} dense />
                            <DetailField label="Budget" value={enq.budget} dense />
                            <DetailField
                              label="Estimated value"
                              value={
                                enq.estimatedValue !== undefined && enq.estimatedValue !== null
                                  ? String(enq.estimatedValue)
                                  : undefined
                              }
                              dense
                            />
                            <DetailField label="Notes" value={enq.notes} dense />
                          </Stack>
                          {Array.isArray(enq.tags) && enq.tags.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                              {enq.tags.map((t: string) => (
                                <Chip key={t} size="small" label={t} />
                              ))}
                            </Box>
                          )}
                          {Array.isArray(enq.customAttributes) &&
                            enq.customAttributes.length > 0 && (
                              <Box sx={{ mt: 0.75 }}>
                                <CustomFieldValues
                                  attributes={enq.customAttributes}
                                  definitions={leadFieldDefs}
                                  dense
                                />
                              </Box>
                            )}
                        </Box>
                      ))}
                      {(enquiryHistoryTotal[detailLead._id] ?? 0) >
                        (enquiryHistory[detailLead._id]?.length ?? 0) && (
                        <Button
                          size="small"
                          onClick={() => loadMoreEnquiryHistory(detailLead._id)}
                          disabled={Boolean(enquiryLoading[detailLead._id])}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          {enquiryLoading[detailLead._id] ? 'Loading…' : 'Load more'}
                        </Button>
                      )}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailLead(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Assign dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Assign {leadWord.singular}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Team Member</InputLabel>
            <Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              label="Team Member"
            >
              {teamMembers.map((m) => (
                <MenuItem key={m._id} value={m._id}>
                  {m.firstName} {m.lastName} ({m.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignLead}
            disabled={!selectedUserId || assigning}
          >
            {assigning ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge duplicate leads dialog */}
      <Dialog
        open={mergeDialogOpen}
        onClose={() => !merging && setMergeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Merge {leadWord.plural}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All {selectedLeadsForMerge.length} selected {leadWord.plural.toLowerCase()} will be
            combined into the one you choose below — their enquiry histories merge into a single
            timeline, and the others are removed. Pick which one to keep:
          </Typography>
          {mergeError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMergeError('')}>
              {mergeError}
            </Alert>
          )}
          <RadioGroup value={mergePrimaryId} onChange={(e) => setMergePrimaryId(e.target.value)}>
            {selectedLeadsForMerge.map((lead) => (
              <FormControlLabel
                key={lead._id}
                value={lead._id}
                control={<Radio />}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mx: 0,
                  mb: 1,
                  py: 0.5,
                  px: 1,
                }}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {lead.customerName || 'Unknown'}
                      {lead._id === selectedLeadsForMerge[0]?._id && (
                        <Chip label="Oldest" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {lead.customerPhone
                        ? formatPhoneDisplay(lead.customerPhone)
                        : lead.customerEmail || '—'}
                      {' · '}
                      {lead.status}
                      {' · created '}
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialogOpen(false)} disabled={merging}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMergeLeads}
            disabled={!mergePrimaryId || merging}
          >
            {merging ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Merge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add lead dialog */}
      <Dialog
        open={addLeadDialogOpen}
        onClose={() => !addingLead && setAddLeadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            Add {leadWord.singular.toLowerCase()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Phone is required. Everything else can be filled in later. A repeat phone number merges
            into the existing {leadWord.singular.toLowerCase()}.
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2.5 }}>
          {addError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddError('')}>
              {addError}
            </Alert>
          )}
          <Stack spacing={3.5}>
            <FormSection title="Contact">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Labeled label="Full name">
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. Priya Sharma"
                      value={newLead.customerName}
                      onChange={(e) => setNewLead({ ...newLead, customerName: e.target.value })}
                    />
                  </Labeled>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PhoneField
                    label="Phone"
                    required
                    size="small"
                    value={newLead.customerPhone}
                    onChange={(digits) => setNewLead({ ...newLead, customerPhone: digits })}
                    defaultCountry="in"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Labeled label="Email">
                    <TextField
                      fullWidth
                      size="small"
                      type="email"
                      placeholder="name@company.com"
                      value={newLead.customerEmail}
                      onChange={(e) => setNewLead({ ...newLead, customerEmail: e.target.value })}
                    />
                  </Labeled>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Labeled label="Company">
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Company name"
                      value={newLead.customerCompany}
                      onChange={(e) => setNewLead({ ...newLead, customerCompany: e.target.value })}
                    />
                  </Labeled>
                </Grid>
              </Grid>
            </FormSection>

            <FormSection title="Enquiry">
              <Stack spacing={2}>
                <Labeled label="Title">
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. Website pricing enquiry"
                    value={newLead.title}
                    onChange={(e) => setNewLead({ ...newLead, title: e.target.value })}
                  />
                </Labeled>
                <Labeled label="Interest" hint="What they want — scoring reads this.">
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. Family package to Bali"
                    value={newLead.interest}
                    onChange={(e) => setNewLead({ ...newLead, interest: e.target.value })}
                  />
                </Labeled>
                <Labeled label="Description">
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    placeholder="Details of the enquiry"
                    value={newLead.description}
                    onChange={(e) => setNewLead({ ...newLead, description: e.target.value })}
                  />
                </Labeled>
                <Labeled label="Internal notes">
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    placeholder="Only your team sees this"
                    value={newLead.notes}
                    onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                  />
                </Labeled>
              </Stack>
            </FormSection>

            {leadFieldDefs.length > 0 && (
              <FormSection title="Details">
                <Grid container spacing={2}>
                  <DynamicFields
                    stacked
                    definitions={leadFieldDefs}
                    values={definedValues}
                    onChange={(k, v) => setDefinedValues((p) => ({ ...p, [k]: v }))}
                  />
                </Grid>
              </FormSection>
            )}

            <FormSection title="Organise">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={5}>
                  <Labeled label="Assign to">
                    <Select
                      fullWidth
                      size="small"
                      displayEmpty
                      value={newLead.assignedTo}
                      onChange={(e) => setNewLead({ ...newLead, assignedTo: String(e.target.value) })}
                    >
                      <MenuItem value="">
                        <em>Unassigned</em>
                      </MenuItem>
                      {teamMembers.map((m) => (
                        <MenuItem key={m._id} value={m._id}>
                          {memberName(m)}
                        </MenuItem>
                      ))}
                    </Select>
                  </Labeled>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Labeled label="Tags" hint="Used to segment and filter later.">
                    <Autocomplete
                      multiple
                      freeSolo
                      filterSelectedOptions
                      size="small"
                      options={allTags}
                      value={newLead.tags}
                      onChange={(_, value) => setNewLead((p) => ({ ...p, tags: cleanTags(value) }))}
                      renderTags={(value, getTagProps) =>
                        value.map((tag, index) => (
                          <Chip label={tag} size="small" {...getTagProps({ index })} key={tag} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...(params as any)}
                          placeholder={newLead.tags.length ? '' : 'Type a tag and press Enter'}
                        />
                      )}
                    />
                  </Labeled>
                </Grid>
              </Grid>
            </FormSection>

            <FormSection title="Other custom fields">
              <Stack spacing={1.5}>
                {newLead.customAttributes.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Anything not covered above — add a name and a value.
                  </Typography>
                )}
                {newLead.customAttributes.map((field, index) => (
                  <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={attributeKeys}
                      value={field.key}
                      onChange={(_, value) => updateCustomAttributeField(index, { key: value || '' })}
                      onInputChange={(_, value, reason) => {
                        if (reason === 'input') updateCustomAttributeField(index, { key: value });
                      }}
                      sx={{ flex: 1 }}
                      renderInput={(params) => (
                        <TextField {...(params as any)} placeholder="Field name" />
                      )}
                    />
                    <TextField
                      size="small"
                      placeholder="Value"
                      sx={{ flex: 1 }}
                      value={field.value}
                      onChange={(e) => updateCustomAttributeField(index, { value: e.target.value })}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeCustomAttributeField(index)}
                      sx={{ mt: 0.25 }}
                      aria-label="Remove field"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={addCustomAttributeField}
                  disabled={newLead.customAttributes.length >= MAX_CUSTOM_FIELDS_PER_LEAD}
                  sx={{ alignSelf: 'flex-start', borderRadius: 2 }}
                >
                  Add field
                </Button>
              </Stack>
            </FormSection>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          {(() => {
            const missing = [
              ...(newLead.customerPhone ? [] : ['Phone']),
              ...leadFieldDefs
                .filter((d) => d.required && !String(definedValues[d.key] ?? '').trim())
                .map((d) => d.label),
            ];
            return (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {missing.length ? `Required: ${missing.join(', ')}` : ''}
                </Typography>
                <Button onClick={() => setAddLeadDialogOpen(false)} disabled={addingLead}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddLead}
                  disabled={missing.length > 0 || addingLead}
                  sx={{ borderRadius: 2, minWidth: 130 }}
                >
                  {addingLead ? (
                    <CircularProgress size={16} sx={{ color: 'inherit' }} />
                  ) : (
                    `Add ${leadWord.singular.toLowerCase()}`
                  )}
                </Button>
              </>
            );
          })()}
        </DialogActions>
      </Dialog>

      {/* ── Bulk assign dialog ───────────────────────────────────────────────── */}
      <Dialog
        open={bulkLeadAssignOpen}
        onClose={() => !bulkLeadAssignSaving && setBulkLeadAssignOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Assign to…</DialogTitle>
        <DialogContent>
          {bulkLeadAssignError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {bulkLeadAssignError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Assign{' '}
            <strong>
              {selectedLeadIds.size} selected {selectedLeadIds.size === 1 ? leadWord.singular.toLowerCase() : leadWord.plural.toLowerCase()}
            </strong>{' '}
            to a team member.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="bulk-lead-assign-user-label">Team member</InputLabel>
            <Select
              labelId="bulk-lead-assign-user-label"
              label="Team member"
              value={bulkLeadAssignUserId}
              onChange={(e) => setBulkLeadAssignUserId(e.target.value)}
            >
              {teamMembers.map((m) => (
                <MenuItem key={m._id} value={m._id}>
                  {`${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkLeadAssignOpen(false)} disabled={bulkLeadAssignSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkAssignLeads}
            variant="contained"
            disabled={bulkLeadAssignSaving || !bulkLeadAssignUserId}
          >
            {bulkLeadAssignSaving ? <CircularProgress size={18} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Quick assign dialog (single row) ────────────────────────────────── */}
      <Dialog
        open={Boolean(quickAssignLead)}
        onClose={() => !quickAssignLeadSaving && setQuickAssignLead(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Assign {leadWord.singular}</DialogTitle>
        <DialogContent>
          {quickAssignLeadError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {quickAssignLeadError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Assign{' '}
            <strong>
              {quickAssignLead?.customerName || quickAssignLead?.customerPhone || `this ${leadWord.singular.toLowerCase()}`}
            </strong>{' '}
            to:
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="quick-lead-assign-user-label">Team member</InputLabel>
            <Select
              labelId="quick-lead-assign-user-label"
              label="Team member"
              value={quickAssignLeadUserId}
              onChange={(e) => setQuickAssignLeadUserId(e.target.value)}
            >
              {teamMembers.map((m) => (
                <MenuItem key={m._id} value={m._id}>
                  {`${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickAssignLead(null)} disabled={quickAssignLeadSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleQuickAssignLead}
            variant="contained"
            disabled={quickAssignLeadSaving || !quickAssignLeadUserId}
          >
            {quickAssignLeadSaving ? <CircularProgress size={18} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inline assignee picker (the Assigned To column) */}
      <Menu
        anchorEl={assignMenu?.anchor ?? null}
        open={Boolean(assignMenu)}
        onClose={() => setAssignMenu(null)}
        PaperProps={{ sx: { maxHeight: 340, minWidth: 220, borderRadius: 2 } }}
      >
        <ListSubheader sx={{ lineHeight: '32px', fontSize: 12, fontWeight: 700 }}>
          Assign to
        </ListSubheader>
        {teamMembers.map((m) => (
          <MenuItem
            key={m._id}
            dense
            selected={m._id === assignMenu?.lead.assignedTo}
            onClick={() => assignMenu && assignInline(assignMenu.lead, m._id)}
          >
            <Avatar
              sx={{
                width: 22,
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                mr: 1.25,
                bgcolor: alpha('#4f46e5', 0.12),
                color: '#4f46e5',
              }}
            >
              {memberName(m)[0]?.toUpperCase()}
            </Avatar>
            {memberName(m)}
          </MenuItem>
        ))}
        {teamMembers.length === 0 && (
          <MenuItem disabled dense>
            No team members found
          </MenuItem>
        )}
        {assignMenu?.lead.assignedTo && <Divider />}
        {assignMenu?.lead.assignedTo && (
          <MenuItem dense onClick={() => assignInline(assignMenu.lead, null)} sx={{ color: 'text.secondary' }}>
            Unassign
          </MenuItem>
        )}
      </Menu>

      <Snackbar
        open={Boolean(actionErrorMsg)}
        autoHideDuration={6000}
        onClose={() => setActionErrorMsg('')}
      >
        <Alert severity="error" onClose={() => setActionErrorMsg('')}>
          {actionErrorMsg}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(bulkLeadSuccessMsg)}
        autoHideDuration={4000}
        onClose={() => setBulkLeadSuccessMsg('')}
      >
        <Alert severity="success" onClose={() => setBulkLeadSuccessMsg('')}>
          {bulkLeadSuccessMsg}
        </Alert>
      </Snackbar>

      {/* A lead can legitimately have no contact record (CSV/API-ingested, never
          messaged), so "nothing happened" needs to say why. */}
      <Snackbar
        open={Boolean(leadNavError)}
        autoHideDuration={4000}
        onClose={() => setLeadNavError('')}
      >
        <Alert severity="info" onClose={() => setLeadNavError('')}>
          {leadNavError}
        </Alert>
      </Snackbar>

      {/* Import dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => !importing && setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import {leadWord.plural} from CSV or Excel</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Upload a CSV or Excel (.xlsx) file with columns for a lead's phone or email plus any
              other fields (name, company, enquiry title/description, interest, value, budget,
              notes, tags). Existing leads (matched by phone or email) will be updated rather than
              duplicated. Any other column can be mapped to a custom field below.
            </Typography>
            <Button
              variant="text"
              size="small"
              startIcon={<Download />}
              onClick={downloadLeadsTemplate}
              sx={{ alignSelf: 'flex-start' }}
            >
              Download CSV Template
            </Button>
            <Button variant="outlined" component="label">
              {importFileName || 'Choose CSV or Excel File'}
              <input
                type="file"
                hidden
                accept=".csv,.xlsx,.xls"
                onChange={handleImportFileChange}
              />
            </Button>

            {importSheetNames.length > 1 && (
              <FormControl size="small" sx={{ maxWidth: 320 }}>
                <InputLabel id="leads-import-sheet-label">Sheet</InputLabel>
                <Select
                  labelId="leads-import-sheet-label"
                  label="Sheet"
                  value={importSelectedSheet}
                  onChange={(e) => handleImportSheetChange(e.target.value)}
                >
                  {importSheetNames.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {importError && <Alert severity="error">{importError}</Alert>}

            {importRows.length > 0 && !importResult && (
              <>
                <Alert severity="info">
                  Found {importRows.length} row{importRows.length === 1 ? '' : 's'}. Review the
                  column mapping below before importing — unmapped columns are skipped.
                </Alert>
                <Autocomplete
                  multiple
                  freeSolo
                  filterSelectedOptions
                  size="small"
                  options={allTags}
                  value={importTags}
                  onChange={(_, value) => setImportTags(cleanTags(value))}
                  renderTags={(value, getTagProps) =>
                    value.map((tag, index) => (
                      <Chip label={tag} size="small" {...getTagProps({ index })} key={tag} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...(params as any)}
                      label="Tags for this import (optional)"
                      placeholder={importTags.length ? '' : 'Type a tag and press Enter…'}
                      helperText="Applied to every lead in this file, in addition to any tags from a mapped CSV column"
                    />
                  )}
                />
                <TableContainer
                  sx={{ maxHeight: 280, border: '1px solid #e2e8f0', borderRadius: 1 }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>CSV Column</TableCell>
                        <TableCell>Maps To</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importHeaders.map((header) => {
                        const selectValue = getImportFieldSelectValue(header);
                        return (
                          <TableRow key={header}>
                            <TableCell>{header}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Select
                                  size="small"
                                  value={selectValue}
                                  onChange={(e) => setImportFieldMapping(header, e.target.value)}
                                  sx={{ minWidth: 170 }}
                                >
                                  {importTargetOptions.map((opt) => (
                                    <MenuItem key={opt.value || 'none'} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {selectValue === CUSTOM_FIELD_SELECT_VALUE && (
                                  <Autocomplete
                                    freeSolo
                                    size="small"
                                    options={attributeKeys}
                                    value={(importMapping[header] || '').slice(
                                      CUSTOM_FIELD_MAPPING_PREFIX.length
                                    )}
                                    onChange={(_, value) =>
                                      setImportCustomFieldKey(header, value || '')
                                    }
                                    onInputChange={(_, value, reason) => {
                                      if (reason === 'input')
                                        setImportCustomFieldKey(header, value);
                                    }}
                                    sx={{ minWidth: 180 }}
                                    renderInput={(params) => (
                                      <TextField {...(params as any)} placeholder="Field name" />
                                    )}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {importResult && (
              <Alert severity={importResult.skipped > 0 ? 'warning' : 'success'}>
                Imported {importResult.imported}, updated {importResult.updated}
                {importResult.skipped > 0 ? `, skipped ${importResult.skipped}` : ''}.
                {importResult.errors.length > 0 && (
                  <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                    {importResult.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>
                        <Typography variant="caption">
                          Row {err.row}: {err.error}
                        </Typography>
                      </li>
                    ))}
                    {importResult.errors.length > 5 && (
                      <li>
                        <Typography variant="caption">
                          …and {importResult.errors.length - 5} more
                        </Typography>
                      </li>
                    )}
                  </Box>
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={importing}>
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          {!importResult && (
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={
                importing ||
                importRows.length === 0 ||
                !Object.values(importMapping).some(
                  (v) => v === 'customerPhone' || v === 'customerEmail'
                )
              }
              startIcon={<UploadFile />}
            >
              {importing ? (
                <CircularProgress size={16} sx={{ color: 'inherit' }} />
              ) : (
                `Import ${importRows.length || ''}`
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(whyAnchor)}
        anchorEl={whyAnchor}
        onClose={() => setWhyAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 260, maxWidth: 340 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Why this score?
          </Typography>
          {whyLoading ? (
            <CircularProgress size={18} />
          ) : !whyData || whyData.breakdown.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No rules matched, or this lead has not been scored yet.
            </Typography>
          ) : (
            <>
              {whyData.breakdown.map((b, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
                  <Typography variant="body2">{b.label}</Typography>
                  <Typography variant="body2" fontWeight={700} color={b.points < 0 ? 'error.main' : 'success.main'}>
                    {b.points > 0 ? '+' : ''}
                    {b.points}
                  </Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #eef1f6', mt: 1, pt: 1 }}>
                <Typography variant="body2" fontWeight={700}>
                  Total (0–100)
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {whyData.score}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default Leads;
